<template>
  <div dir="rtl" class="mr-8 px-4 sm:px-6 lg:px-8">
    <!-- Alert -->
    <div v-if="alert.show" 
         :class="[
           'mb-6 p-4 rounded-md border',
           alert.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : '',
           alert.type === 'danger' ? 'bg-red-50 border-red-200 text-red-800' : '',
           alert.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' : ''
         ]">
      {{ alert.message }}
    </div>

    <!-- Page Header -->
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">إدارة الجهات</h1>
        <p class="mt-2 text-sm text-gray-700">إدارة جهات التحفيظ والمؤسسات التعليمية</p>
      </div>
      <div class="mt-4 sm:mt-0 sm:mr-16 sm:flex-none">
        <button
          @click="openAddModal"
          class="px-4 py-2 bg-customHafadhColor text-white rounded hover:bg-customHafadhColorHover transition-colors">
          إضافة جهة جديدة
        </button>
      </div>
    </div>

    <!-- Search Section -->
    <div class="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      <div class="relative">
        <input
          v-model="searchTerm"
          type="text"
          placeholder="البحث في الجهات..."
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm pr-3"
        />
      </div>
      <div class="text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-lg flex items-center">
        إجمالي الجهات: {{ organizations.length }}
      </div>
    </div>

    <!-- Organizations Table -->
    <div class="mt-8 flex flex-col">
      <div class="sm:flex-auto">
        <h1 class="text-lg font-medium text-gray-900">قائمة الجهات</h1>
        <p class="mt-2 text-xs text-gray-700"></p>
      </div>

      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p class="mt-2 text-gray-600">جاري التحميل...</p>
      </div>

      <div v-else-if="filteredOrganizations.length === 0" class="text-center py-12">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m0 0h2M7 5h10"></path>
        </svg>
        <p class="mt-2 text-gray-600">
          {{ searchTerm ? 'لا توجد جهات تطابق البحث' : 'لا توجد جهات مسجلة' }}
        </p>
      </div>

      <div v-else class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
          <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table class="min-w-full divide-y divide-customHafadhColor">
              <thead class="bg-customHafadhColor text-center">
                <tr>
                  <th class="py-3.5 pr-4 pl-3 text-right text-sm font-semibold text-gray-900 sm:pr-6">#</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">الشعار</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">اسم الجهة</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">تاريخ الإنشاء</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">آخر تحديث</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">تعديل</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">حذف</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <tr v-for="(org, index) in filteredOrganizations" :key="org.id">
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm text-gray-900 sm:pr-6">{{ index + 1 }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    <div v-if="org.logoUrl || org.logoBase64" class="h-10 w-10 flex-shrink-0">
                      <img
                        :src="org.logoUrl || `data:image/jpeg;base64,${org.logoBase64}`"
                        :alt="org.name"
                        class="h-10 w-10 rounded-lg object-cover"
                      />
                    </div>
                    <div v-else class="h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <svg class="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                    </div>
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">{{ org.name }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ formatDate(org.createdAt) }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ formatDate(org.updatedAt) }}</td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium sm:pr-6">
                    <button class="enabled-button" @click="openEditModal(org)">تعديل</button>
                  </td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium sm:pr-6">
                    <button class="text-red-600 hover:text-red-800" @click="handleDelete(org)">حذف</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <div v-if="showModal" class="popup" dir="rtl">
      <div class="popup-content">
        <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
          {{ editMode ? 'تعديل الجهة' : 'إضافة جهة جديدة' }}
        </h3>
        
        <form @submit.prevent="handleSave">
          <div class="grid grid-cols-1 gap-4">
            <div>
              <label for="name" class="block text-sm font-medium text-gray-700 mb-2">اسم الجهة *</label>
              <input
                v-model="formData.name"
                type="text"
                id="name"
                required
                placeholder="أدخل اسم الجهة"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label for="logo" class="block text-sm font-medium text-gray-700 mb-2">شعار الجهة</label>
              <input
                @change="handleLogoChange"
                type="file"
                id="logo"
                accept="image/*"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              <p class="text-xs text-gray-500 mt-1">يرجى اختيار صورة مربعة للحصول على أفضل النتائج (أقل من 5 ميجابايت)</p>
            </div>

            <!-- Logo Preview -->
            <div v-if="formData.logoPreview">
              <label class="block text-sm font-medium text-gray-700 mb-2">معاينة الشعار:</label>
              <div class="text-center">
                <img
                  :src="formData.logoPreview"
                  alt="معاينة الشعار"
                  class="max-w-48 max-h-48 object-cover rounded-lg border mx-auto"
                />
              </div>
            </div>
          </div>

          <div class="mt-6 grid grid-cols-2 gap-3">
            <button
              type="submit"
              :disabled="saving"
              class="enabled-button w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm">
              <div v-if="saving" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
              {{ editMode ? 'تحديث' : 'إضافة' }}
            </button>
            <button
              type="button"
              @click="closeModal"
              :disabled="saving"
              class="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm">
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import url from '@/url';

export default {
  name: 'Organizations',
  data() {
    return {
      organizations: [],
      searchTerm: '',
      loading: true,
      showModal: false,
      editMode: false,
      currentOrg: null,
      saving: false,
      alert: {
        show: false,
        message: '',
        type: ''
      },
      formData: {
        name: '',
        logoFile: null,
        logoPreview: null
      }
    };
  },
  computed: {
    filteredOrganizations() {
      if (!this.searchTerm.trim()) {
        return this.organizations;
      }
      return this.organizations.filter(org =>
        org.name.toLowerCase().includes(this.searchTerm.toLowerCase())
      );
    }
  },
  mounted() {
    this.fetchOrganizations();
  },
  methods: {
    async fetchOrganizations() {
      try {
        this.loading = true;
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.organizations,
          {
            headers: {
              'X-Authorization': localStorage.getItem('token'),
            }
          }
        );
        
        if (response.data.result?.status === 200) {
          this.organizations = response.data.result.data || [];
        } else {
          this.showAlert('خطأ في جلب الجهات', 'danger');
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
        this.showAlert('خطأ في الاتصال بالخادم', 'danger');
      } finally {
        this.loading = false;
      }
    },

    showAlert(message, type = 'info') {
      this.alert = { show: true, message, type };
      setTimeout(() => {
        this.alert = { show: false, message: '', type: '' };
      }, 5000);
    },

    openAddModal() {
      this.resetForm();
      this.editMode = false;
      this.showModal = true;
    },

    openEditModal(org) {
      this.currentOrg = org;
      this.formData = {
        name: org.name,
        logoFile: null,
        logoPreview: org.logoUrl || `data:image/jpeg;base64,${org.logoBase64}`
      };
      this.editMode = true;
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.resetForm();
    },

    resetForm() {
      this.formData = {
        name: '',
        logoFile: null,
        logoPreview: null
      };
      this.editMode = false;
      this.currentOrg = null;
    },

    handleLogoChange(event) {
      const file = event.target.files[0];
      if (file) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          this.showAlert('يرجى اختيار ملف صورة صالح', 'warning');
          return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          this.showAlert('حجم الملف كبير جداً. يرجى اختيار صورة أقل من 5 ميجابايت', 'warning');
          return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
          this.formData.logoFile = file;
          this.formData.logoPreview = event.target.result;
        };
        reader.readAsDataURL(file);
      }
    },

    async handleSave() {
      if (!this.formData.name.trim()) {
        this.showAlert('يرجى إدخال اسم الجهة', 'warning');
        return;
      }

      this.saving = true;

      try {
        const requestData = {
          name: this.formData.name.trim(),
          logoBase64: null,
          logoUrl: null
        };

        // Convert image to base64 if file is selected
        if (this.formData.logoFile) {
          const reader = new FileReader();
          reader.onload = async (event) => {
            requestData.logoBase64 = event.target.result.split(',')[1]; // Remove data:image/jpeg;base64, prefix
            await this.submitForm(requestData);
          };
          reader.readAsDataURL(this.formData.logoFile);
        } else if (this.formData.logoPreview && !this.editMode) {
          this.showAlert('يرجى اختيار شعار للجهة', 'warning');
          this.saving = false;
          return;
        } else {
          await this.submitForm(requestData);
        }
      } catch (error) {
        console.error('Error saving organization:', error);
        this.showAlert('خطأ في حفظ الجهة', 'danger');
        this.saving = false;
      }
    },

    async submitForm(requestData) {
      try {
        let response;
        
        if (this.editMode && this.currentOrg) {
          response = await axios.put(
            process.env.VUE_APP_SERVICE_URL + url.organizations + '/' + this.currentOrg.id,
            requestData,
            {
              headers: {
                'X-Authorization': localStorage.getItem('token'),
              }
            }
          );
        } else {
          response = await axios.post(
            process.env.VUE_APP_SERVICE_URL + url.organizations,
            requestData,
            {
              headers: {
                'X-Authorization': localStorage.getItem('token'),
              }
            }
          );
        }

        if (response.data.result?.status === (this.editMode ? 200 : 201)) {
          this.showAlert(
            this.editMode ? 'تم تحديث الجهة بنجاح' : 'تم إضافة الجهة بنجاح',
            'success'
          );
          this.closeModal();
          this.fetchOrganizations();
        } else {
          this.showAlert(response.data.result?.mesAr || 'خطأ في العملية', 'danger');
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        if (error.response?.data?.result?.mesAr) {
          this.showAlert(error.response.data.result.mesAr, 'danger');
        } else {
          this.showAlert('خطأ في العملية', 'danger');
        }
      } finally {
        this.saving = false;
      }
    },

    async handleDelete(org) {
      if (confirm(`هل أنت متأكد من حذف الجهة "${org.name}"؟`)) {
        try {
          this.loading = true;
          const response = await axios.delete(
            process.env.VUE_APP_SERVICE_URL + url.organizations + '/' + org.id,
            {
              headers: {
                'X-Authorization': localStorage.getItem('token'),
              }
            }
          );
          
          if (response.data.result?.status === 200) {
            this.showAlert('تم حذف الجهة بنجاح', 'success');
            this.fetchOrganizations();
          } else {
            this.showAlert(response.data.result?.mesAr || 'خطأ في حذف الجهة', 'danger');
          }
        } catch (error) {
          console.error('Error deleting organization:', error);
          this.showAlert('خطأ في حذف الجهة', 'danger');
        } finally {
          this.loading = false;
        }
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('ar-SA');
    }
  }
};
</script>

<style scoped>
.enabled-button {
  @apply px-4 py-2 bg-customHafadhColor text-white rounded hover:bg-customHafadhColor w-20 h-10;
}

.popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.popup-content {
  background-color: #fff;
  padding: 20px;
  border-radius: 5px;
  width: 540px;
  text-align: center;
}
</style> 